import { useAuth0 } from '@auth0/auth0-react';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import { createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import useDetectRateMode from 'components/Dashboard/ListControl/useDetectRateMode';
import RatingUpdateBadge from 'components/Dashboard/PriorityUpdates/RatingUpdateBadge';
import { RateUpdateButtons } from 'components/Dashboard/RateUpdatesFeed/RateUpdatesContent';
import useRatingUpdatesFunction from 'components/Dashboard/RateUpdatesFeed/useRatingUpdatesFunction';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import transformToPascalCase from 'components/Settings/Utilities/TransformToPascalCase';
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const RateUpdatesContentButton = ({ currentNew }) => {
  const [isOpen, setIsOpen] = useState(false);
  const rating = currentNew?.feedback?.importanceRating ?? null;
  const { contentResults, setContentResults, setFeedState } = useContentResults();
  const { ratingUpdateFunction } = useRatingUpdatesFunction();

  const actionsElement = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const buttonNotRef = useRef(null);
  const buttonQuiteRef = useRef(null);
  const buttonVeryRef = useRef(null);

  const hideActions = (targetElement) => {
    if (dropdownRef.current && targetElement && dropdownRef.current.contains(targetElement)) return;
    setIsOpen(false);
  };
  useOutsideElement(actionsElement, hideActions);

  useLayoutEffect(() => {
    if (!isOpen || !actionsElement.current) return;
    const rect = actionsElement.current.getBoundingClientRect();
    const margin = 16;
    const dropdownWidth = dropdownRef.current?.offsetWidth ?? 0;
    const left = dropdownWidth
      ? Math.max(margin, Math.min(rect.left, window.innerWidth - dropdownWidth - margin))
      : rect.left;
    setDropdownStyle({ position: 'fixed', top: rect.bottom + 5, left, width: 'max-content', maxWidth: '670px' });
  }, [isOpen]);
  const sectionName = createSectionName(true);
  const section = contentResults[sectionName];
  const rateUpdate = async ({ rating }) => {
    if (!!currentNew) {
      const newSection = {
        ...section,
        new: {
          ...currentNew,
          feedback: {
            ...currentNew.feedback,
            importanceRating: rating,
          },
        },
      };
      let newContentResults = {
        ...contentResults,
        [sectionName]: newSection,
      };
      setContentResults(newContentResults);

      let targetButton;
      if (rating === 'Not important') {
        targetButton = buttonNotRef.current;
      } else if (rating === 'Quite important') {
        targetButton = buttonQuiteRef.current;
      } else if (rating === 'Very important') {
        targetButton = buttonVeryRef.current;
      }

      if (targetButton && !targetButton.classList.contains('rate-active')) {
        const targetClass = `rate-${rating.split(' ')[0].toLowerCase()}`;
        targetButton.classList.add('rate-active', targetClass);

        const results = await ratingUpdateFunction({
          objectId: currentNew.objectId,
          importanceRating: transformToPascalCase(rating),
        });

        if (results?.status === 200) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          targetButton.classList.remove('rate-active', targetClass);
          setIsOpen(false);
          if (rating === 'Not important') {
            setFeedState('feedbackItemPopup', {
              ratingScored: 'Negative',
              isOpen: true,
              item: { objectID: currentNew?.objectId },
              keywords: currentNew?.keywordsFromLists,
              contentPage: true,
              firstTime: true,
            });
          }
        }
      }
    }
  };

  const { rateUpdatesMode } = useDetectRateMode();
  const { isAuthenticated } = useAuth0();

  return (
    <>
      {!rateUpdatesMode && isAuthenticated && (
        <div ref={actionsElement} className='position-relative'>
          <button
            className={`general-button border rounded-pill h-100 specific-left-padding-12 pr-3 specific-y-padding-5 paragraph-p2 hovered-button ${isOpen ? 'bg-blue-hovered' : ''}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className={`dropdown-item-element dropdown-item-element-9 ${isOpen ? 'open' : ''}`}>
              {rating ? (
                <RatingUpdateBadge content={true} item={{ rating: TransformFromPascalCase(rating) }} />
              ) : (
                <>How important is this update to you?</>
              )}
            </span>
          </button>
          {isOpen &&
            createPortal(
              <div
                ref={dropdownRef}
                className='rounded box-shadow px-4 py-3 z-extra-max'
                style={dropdownStyle ?? { visibility: 'hidden', position: 'fixed', width: 'max-content', maxWidth: '670px' }}
              >
                <RateUpdateButtons
                  rateUpdate={rateUpdate}
                  rating={TransformFromPascalCase(rating)}
                  buttonRefs={{ buttonNotRef, buttonQuiteRef, buttonVeryRef }}
                  contentPage={true}
                />
              </div>,
              document.body
            )}
        </div>
      )}
    </>
  );
};

export default RateUpdatesContentButton;
